/**
 * Maps normalized game-rect coords (0–1) to canvas pixels.
 *
 * Layout rules:
 *  - **fruit-ninja-without-score** (`useNativeAspectRatio` false): game rect is always 16:9;
 *    `aspectRatio` from JSON is ignored.
 *  - **target-attack** (`useNativeAspectRatio` true): game rect uses `aspectRatio` from JSON
 *    when present and valid; if omitted, **16/9 ≈ 1.77** (same default).
 *  - Canvas taller-than-or-equal to the game aspect: game fills full width, bottom-aligned.
 *    Top strip (offsetY px) is the HUD zone.
 *  - Canvas wider than that: game is centred horizontally (offsetX > 0) and fills
 *    full height. Left/right panels are drawn as decorative out-of-bounds zones.
 */
export interface RemoteViewLayout {
  readonly offsetX: number;
  readonly offsetY: number;
  readonly gameWidth: number;
  readonly gameHeight: number;
  /** Target draw size baseline in px (PNG fruit uses fruitRadiusPx derived from this). */
  readonly fruitFontPx: number;
  /** Half-width of on-screen fruit in px (PNG + slice particles). */
  readonly fruitRadiusPx: number;
}

const DEFAULT_GAME_ASPECT = 16 / 9;

export interface ComputeRemoteViewLayoutOptions {
  /**
   * When true (target-attack), letterbox uses `aspectRatio` from native JSON when set;
   * otherwise **16/9**. When false, geometry is always 16:9 regardless of JSON.
   */
  readonly useNativeAspectRatio?: boolean;
}

function resolveGameAspect(
  aspectRatio: number | undefined,
  useNativeAspectRatio: boolean | undefined,
): number {
  if (useNativeAspectRatio === true) {
    if (aspectRatio != null && Number.isFinite(aspectRatio) && aspectRatio > 0) {
      return aspectRatio;
    }
    return DEFAULT_GAME_ASPECT;
  }
  return DEFAULT_GAME_ASPECT;
}

/**
 * Computes the game area rectangle. `fruitSize` scales fruit / target visuals.
 */
export function computeRemoteViewLayout(
  canvasW: number,
  canvasH: number,
  aspectRatio: number | undefined,
  fruitSize: number | undefined,
  options?: ComputeRemoteViewLayoutOptions,
): RemoteViewLayout {
  const fs =
    fruitSize != null && Number.isFinite(fruitSize) && fruitSize > 0 ? fruitSize : 0.1;

  let gw: number;
  let gh: number;
  let ox: number;
  let oy: number;

  const gameAspect = resolveGameAspect(aspectRatio, options?.useNativeAspectRatio);
  const idealHeight = canvasW / gameAspect;

  if (idealHeight <= canvasH) {
    // Canvas is taller-or-equal → full width, bottom-aligned, top strip for HUD
    gw = canvasW;
    gh = idealHeight;
    ox = 0;
    oy = canvasH - gh;
  } else {
    // Canvas is wider → centre game horizontally; side panels = out-of-bounds zone
    gh = canvasH;
    gw = gh * gameAspect;
    ox = (canvasW - gw) / 2;
    oy = 0;
  }

  const FRUIT_VISUAL_SCALE = 2;
  const fruitFontPx = Math.max(24, Math.min(fs * gh * FRUIT_VISUAL_SCALE, gh * 0.45));
  const fruitRadiusPx = fruitFontPx * 0.525;

  return {
    offsetX: ox,
    offsetY: oy,
    gameWidth: gw,
    gameHeight: gh,
    fruitFontPx,
    fruitRadiusPx,
  };
}

/**
 * Layout when native omits layout fields entirely — same 16:9 bottom-align logic.
 */
export function defaultRemoteViewLayout(canvasW: number, canvasH: number): RemoteViewLayout {
  return computeRemoteViewLayout(canvasW, canvasH, undefined, undefined);
}

/**
 * Two-player split: same vertical game height and fruit/target scale as the full-width layout,
 * each player gets half of the horizontal game rect (vertical cut through the middle).
 */
export function computeSplitRemoteViewLayouts(
  canvasW: number,
  canvasH: number,
  fruitSize: number | undefined,
  aspectRatio?: number,
  options?: ComputeRemoteViewLayoutOptions,
): { left: RemoteViewLayout; right: RemoteViewLayout; full: RemoteViewLayout } {
  const full = computeRemoteViewLayout(canvasW, canvasH, aspectRatio, fruitSize, options);
  const gw2 = full.gameWidth / 2;
  const left: RemoteViewLayout = {
    offsetX: full.offsetX,
    offsetY: full.offsetY,
    gameWidth: gw2,
    gameHeight: full.gameHeight,
    fruitFontPx: full.fruitFontPx,
    fruitRadiusPx: full.fruitRadiusPx,
  };
  const right: RemoteViewLayout = {
    offsetX: full.offsetX + gw2,
    offsetY: full.offsetY,
    gameWidth: gw2,
    gameHeight: full.gameHeight,
    fruitFontPx: full.fruitFontPx,
    fruitRadiusPx: full.fruitRadiusPx,
  };
  return { left, right, full };
}
